import { useCallback, useEffect, useState } from 'react';
import { useQzTray } from 'src/hooks/useQzTray';
import { useQzPrint } from 'src/hooks/useQzPrint';
import {
  PRINT_DOC_CONFIGS,
  PrintDocType,
  PrinterType,
  usePrinterSettingStore,
} from 'src/store/usePrinterSettingStore';
import { usePrinterSettingModalStore } from './usePrinterSettingModalStore';
import { showSuccess, showWarning } from 'src/utils/toast/toastUtil';

/** QZ Tray 연결 상태에 따른 뱃지 표시 (연결 중 / 연결됨 / 연결 안됨) */
const ConnectionBadge = ({
  isConnected,
  isConnecting,
}: {
  isConnected: boolean;
  isConnecting: boolean;
}) => {
  if (isConnecting)
    return <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700">연결 중...</span>;
  if (isConnected)
    return <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">● 연결됨</span>;
  return <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">● 연결 안됨</span>;
};

interface PrinterSettingContentProps {
  onClose?: () => void;
}

/** 프린터 설정 모달 본문: QZ Tray 연동, 문서별 프린터/타입 지정, 저장·초기화·테스트 출력 */
export const PrinterSettingContent = ({ onClose }: PrinterSettingContentProps) => {
  const {
    isConnected, isConnecting, printerList, defaultPrinter, error, connect, refreshPrinters,
  } = useQzTray();

  const { settings, typeSettings, setSetting, setTypeSetting, saveSettings, resetSettings } =
    usePrinterSettingStore();
  const { printerFilter, setPrinterFilter } = usePrinterSettingModalStore();
  const { printWithPrinterName } = useQzPrint();

  const [localSettings, setLocalSettings] = useState<Record<string, string>>({ ...settings });
  const [localTypeSettings, setLocalTypeSettings] = useState<Record<string, PrinterType>>({
    ...typeSettings,
  });
  const [isDirty, setIsDirty] = useState(false);

  /** 저장된 설정이 바뀌면 로컬 편집값과 dirty 플래그를 동기화 */
  useEffect(() => {
    setLocalSettings({ ...settings });
    setLocalTypeSettings({ ...typeSettings });
    setIsDirty(false);
  }, [settings, typeSettings]);

  /** 문서 종류별 선택 프린터 변경 시 로컬 설정 갱신 및 dirty 표시 */
  const handleChange = useCallback((docType: PrintDocType, printerName: string) => {
    setLocalSettings(prev => ({ ...prev, [docType]: printerName }));
    setIsDirty(true);
  }, []);

  /** 라벨 문서(label1/label2)의 프린터 타입(라벨/일반) 변경 시 로컬 설정 갱신 및 dirty 표시 */
  const handleTypeChange = useCallback((docType: 'label1' | 'label2', printerType: PrinterType) => {
    setLocalTypeSettings(prev => ({ ...prev, [docType]: printerType }));
    setIsDirty(true);
  }, []);

  /** 로컬 설정을 전역 스토어에 반영 후 저장하고 토스트 표시, 저장 후 팝업 닫기 */
  const handleSave = useCallback(() => {
    Object.entries(localSettings).forEach(([docType, printerName]) => {
      setSetting(docType as PrintDocType, printerName);
    });
    Object.entries(localTypeSettings).forEach(([docType, printerType]) => {
      setTypeSetting(docType as 'label1' | 'label2', printerType as PrinterType);
    });
    saveSettings();
    setIsDirty(false);
    showSuccess('프린터 설정이 저장되었습니다.');
    onClose?.();
  }, [localSettings, localTypeSettings, setSetting, setTypeSetting, saveSettings, onClose]);

  /** 확인 후 프린터 설정 초기화 및 토스트 표시 */
  const handleReset = useCallback(() => {
    if (!window.confirm('프린터 설정을 초기화하시겠습니까?')) return;
    resetSettings();
    showWarning('프린터 설정이 초기화되었습니다.');
  }, [resetSettings]);

  /** 선택한 문서 종류로 테스트 HTML 생성 후 해당 프린터로 출력 (라벨/일반 구분) */
  const handleTestPrint = useCallback(async (docType: PrintDocType) => {
    const printerName = localSettings[docType];
    if (!printerName) {
      showWarning('프린터를 먼저 선택해주세요.');
      return;
    }

    const isLabel = docType === 'label1' || docType === 'label2';
    // 라벨 문서일 때만 타입(라벨/일반) 적용
    const printerType = isLabel
      ? (localTypeSettings[docType] as PrinterType)
      : 'general';

    /** 라벨 프린터일 때만 40x25mm 크기 지정 (일반 프린터는 미지정) */
    const labelSize = isLabel && printerType === 'label'
      ? { width: 40, height: 25 }
      : undefined;

    const testHtml = isLabel
      ? `<html>
           <body style="margin:0;padding:2mm;font-family:Arial;font-size:8pt;
                        ${printerType === 'label' ? 'width:40mm;height:25mm;' : 'width:210mm;'}
                        overflow:hidden;">
             <div style="border:1px solid #000;padding:2mm;box-sizing:border-box;">
               <div style="font-weight:bold;font-size:9pt;">TEST LABEL</div>
               <div>${docType.toUpperCase()} / ${printerType === 'label' ? '라벨 프린터' : '일반 프린터'}</div>
               <div>${printerName}</div>
               <div>${new Date().toLocaleString()}</div>
             </div>
           </body>
         </html>`
      : `<html>
           <body style="font-family:Arial;font-size:12pt;padding:20mm;">
             <h2>테스트 출력</h2>
             <p>문서 종류: ${docType}</p>
             <p>프린터: ${printerName}</p>
             <p>출력 시각: ${new Date().toLocaleString()}</p>
           </body>
         </html>`;

    const success = await printWithPrinterName(printerName, testHtml, 'html', labelSize);
    if (success) showSuccess(`테스트 출력 완료 (${printerName})`);
  }, [localSettings, localTypeSettings, printWithPrinterName]);

  /** 프린터 이름 검색어로 필터링한 목록 (드롭다운용) */
  const filteredPrinters = printerList.filter(p =>
    p.toLowerCase().includes(printerFilter.toLowerCase())
  );

  return (
    <div className="p-6 max-w-4xl">
      <h2 className="text-lg font-bold mb-1">프린터 설정</h2>
      <p className="text-sm text-gray-500 mb-5">
        문서 종류별 출력 프린터를 지정합니다. 저장 후 해당 프린터로 즉시 출력됩니다.
      </p>

      {/* QZ Tray 연결 상태 */}
      <div className="flex items-center gap-3 p-3 bg-gray-50 border rounded mb-6">
        <ConnectionBadge isConnected={isConnected} isConnecting={isConnecting} />
        <span className="text-sm text-gray-600">
          {isConnecting
            ? 'QZ Tray에 연결 중입니다...'
            : isConnected
            ? `프린터 ${printerList.length}개 감지됨 / 기본 프린터: ${defaultPrinter || '없음'}`
            : 'QZ Tray가 연결되지 않았습니다. QZ Tray 앱이 실행 중인지 확인하세요.'}
        </span>
        <div className="ml-auto flex gap-2">
          {isConnected && (
            <button onClick={refreshPrinters} className="text-xs px-3 py-1 border rounded hover:bg-gray-100">
              🔄 새로고침
            </button>
          )}
          {!isConnected && !isConnecting && (
            <button onClick={connect} className="text-xs px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600">
              재연결
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-600">
          ⚠ {error}
        </div>
      )}

      {isConnected && (
        <div className="mb-3">
          <input
            type="text"
            placeholder="프린터 이름 검색..."
            value={printerFilter}
            onChange={e => setPrinterFilter(e.target.value)}
            className="w-64 border rounded px-3 py-1.5 text-sm"
          />
        </div>
      )}

      {/* 프린터 매핑 테이블 */}
      <table className="w-full border-collapse text-sm mb-4">
        <thead>
          <tr className="bg-gray-100">
            <th className="border px-3 py-2 text-left w-20">구분</th>
            <th className="border px-3 py-2 text-left w-28">문서 종류</th>
            <th className="border px-3 py-2 text-left">프린터 선택</th>
            <th className="border px-3 py-2 text-center w-36">프린터 타입</th>
            <th className="border px-3 py-2 text-center w-20">테스트</th>
          </tr>
        </thead>
        <tbody>
          {PRINT_DOC_CONFIGS.map(({ key, label, description, isLabel }) => {
            const currentPrinter = localSettings[key] || '';
            const currentType = isLabel
              ? (localTypeSettings[key] as PrinterType)
              : 'general';
            const isSaved = settings[key] === currentPrinter && !!currentPrinter;

            return (
              <tr key={key} className="hover:bg-gray-50">
                {/* 구분 */}
                <td className="border px-3 py-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    isLabel ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {isLabel ? '라벨' : '보고서'}
                  </span>
                </td>

                {/* 문서 종류 */}
                <td className="border px-3 py-2">
                  <div className="font-medium">{label}</div>
                  <div className="text-xs text-gray-400">{description}</div>
                </td>

                {/* 프린터 선택 */}
                <td className="border px-3 py-2">
                  <div className="flex items-center gap-2">
                    <select
                      value={currentPrinter}
                      onChange={e => handleChange(key, e.target.value)}
                      disabled={!isConnected}
                      className={`flex-1 border rounded px-2 py-1.5 text-sm ${
                        !isConnected ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                      }`}
                    >
                      <option value="">-- 프린터 선택 --</option>
                      {filteredPrinters.map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                    {currentPrinter && (
                      <span className={`text-xs whitespace-nowrap ${
                        isSaved ? 'text-green-500' : 'text-orange-500'
                      }`}>
                        {isSaved ? '✓ 저장됨' : '● 미저장'}
                      </span>
                    )}
                  </div>
                </td>

                {/* 프린터 타입 (라벨 문서만) */}
                <td className="border px-3 py-2 text-center">
                  {isLabel ? (
                    <div className="flex justify-center gap-2 text-xs">
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          name={`type_${key}`}
                          value="label"
                          checked={currentType === 'label'}
                          onChange={() => handleTypeChange(key as 'label1' | 'label2', 'label')}
                        />
                        <span>라벨</span>
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          name={`type_${key}`}
                          value="general"
                          checked={currentType === 'general'}
                          onChange={() => handleTypeChange(key as 'label1' | 'label2', 'general')}
                        />
                        <span>일반(A4)</span>
                      </label>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">A4 고정</span>
                  )}
                </td>

                {/* 테스트 출력 */}
                <td className="border px-3 py-2 text-center">
                  <button
                    onClick={() => handleTestPrint(key)}
                    disabled={!isConnected || !currentPrinter}
                    className={`text-xs px-2 py-1 rounded ${
                      isConnected && currentPrinter
                        ? 'bg-gray-200 hover:bg-gray-300 cursor-pointer'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    테스트
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* 하단 버튼 */}
      <div className="flex items-center gap-2">
        <button
          onClick={handleSave}
          disabled={!isDirty}
          className={`px-4 py-2 rounded text-sm font-medium ${
            isDirty
              ? 'bg-blue-500 text-white hover:bg-blue-600 cursor-pointer'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          저장
        </button>
        <button
          onClick={handleReset}
          className="px-4 py-2 bg-white border rounded text-sm hover:bg-gray-50"
        >
          초기화
        </button>
        {isDirty && (
          <span className="text-xs text-orange-500 ml-2">
            ● 저장되지 않은 변경사항이 있습니다.
          </span>
        )}
      </div>
    </div>
  );
};

export default PrinterSettingContent;
